import { Immutable } from "immer";
import React from "react";

import type { SharedCustomerReviewsBlock as Value } from "@reactivated";

import { StarRating } from "../StarRating";

// NOTE: This component reproduces the legacy server-rendered markup from
// `tsicommon_cms/blocks/customer-reviews-block.html` verbatim — every classname
// (and the `id="customer-reviews-block"`) is load-bearing for the existing SCSS
// (`_customer-reviews-block.scss`), so keep them in sync.
//
// Notable parity details carried over from the legacy template:
//   - `reviews_title` is a CharBlock → rendered as escaped plain text inside
//     `<header>` (the legacy `{% include_block %}` of a CharBlock emits the
//     escaped string).
//   - `ReviewBlock` has no `date` field, but the legacy markup always emitted an
//     (always-empty) `customer-reviews-block__review-date` span — reproduced as
//     an empty span so the SCSS layout is unchanged.
//   - Star rating reuses the shared `StarRating` component (the Batch-3 landing
//     CustomerReviews precedent). The legacy `star-rating.html` had an
//     `{% if APP_SLUG != 'tempurca' %}` gate, but the shared block is only used
//     on sealy + toppers (never tempurca), so the gate is moot — StarRating
//     always renders, matching the precedent. The legacy `class=ratingClasses`
//     arg was undefined, so no extra className is passed.
//   - title / text / author / reviews_title are CharBlock → escaped TEXT (never
//     dangerouslySetInnerHTML).
type IProps = Immutable<{
    value: Value;
}>;

export const CustomerReviewsBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const reviews = v.reviews ?? [];

    return (
        <div
            id="customer-reviews-block"
            className="l-full-width customer-reviews-block home-page-section"
        >
            <div className="l-capped-width">
                <header>{v.reviews_title}</header>
                <div className="customer-reviews-block__review-list customer-reviews-block__review-list--homepage">
                    {reviews.map((review, i) => (
                        <div key={i} className="customer-reviews-block__review">
                            <div className="customer-reviews-block__review-top">
                                {/* ReviewBlock has no `date` field; the legacy
                                    markup always emitted an empty date span. */}
                                <span className="customer-reviews-block__review-date"></span>
                                <p className="customer-reviews-block__review-headline">
                                    {review.title}
                                </p>
                                <StarRating
                                    rating={parseFloat(review.stars ?? "0")}
                                />
                                <p className="customer-reviews-block__review-text">
                                    {review.text}
                                </p>
                            </div>
                            <div className="customer-reviews-block__review-owner">
                                {review.author}
                            </div>
                        </div>
                    ))}
                </div>
                {v.review_link && (
                    <footer className="customer-reviews-block__foot">
                        <a href={v.review_link.url} className="button">
                            {v.review_link_name}
                        </a>
                    </footer>
                )}
            </div>
        </div>
    );
};
